import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FondoResumen,
  ListarMovimientosFondoResponse,
  RegistrarIngresoFondoRequest,
  RegistrarEgresoFondoRequest,
  MovimientoFondo,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useFondoResumen(condoId: string) {
  return useQuery({
    queryKey: queryKeys.fondoResumen(condoId),
    queryFn: () => api.get<FondoResumen>(ApiRoutes.fondoReserva(condoId)),
    enabled: !!condoId,
  });
}

export function useMovimientosFondo(
  condoId: string,
  opts?: { cursor?: string },
) {
  return useQuery({
    queryKey: [...queryKeys.fondoMovimientos(condoId), opts?.cursor ?? ''],
    queryFn: () =>
      api.get<ListarMovimientosFondoResponse>(
        ApiRoutes.fondoReservaMovimientos(condoId),
        { query: { cursor: opts?.cursor } },
      ),
    enabled: !!condoId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useRegistrarIngresoFondo(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegistrarIngresoFondoRequest) =>
      api.post<{ movimiento: MovimientoFondo }, RegistrarIngresoFondoRequest>(
        ApiRoutes.fondoReservaIngresos(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.fondoResumen(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.fondoMovimientos(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard(condoId) });
    },
  });
}

export function useRegistrarEgresoFondo(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegistrarEgresoFondoRequest) =>
      api.post<{ movimiento: MovimientoFondo }, RegistrarEgresoFondoRequest>(
        ApiRoutes.fondoReservaEgresos(condoId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.fondoResumen(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.fondoMovimientos(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard(condoId) });
    },
  });
}

export interface IntegridadError {
  movimientoId: string;
  indice: number;
  error: string;
}

export interface IntegridadResult {
  valid: boolean;
  totalMovimientos: number;
  saldoActual: number;
  errors: IntegridadError[];
}

export function useVerificarIntegridadFondo(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<IntegridadResult>(ApiRoutes.fondoReservaVerificar(condoId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.fondoResumen(condoId) });
    },
  });
}
